import { Component, computed, DestroyRef, effect, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import {
  AbstractControl,
  UntypedFormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { ApiService } from 'src/app/services';

import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { UtilsService } from 'src/app/services/utils.service';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { WorkingExtraHoursPeriod } from 'src/app/models/working-extra-hours-period.model';
import { CompanyProduct } from 'src/app/models/company_product.model';

function atLeastOneCompanyProductSelectedValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value as number[] | null;

    if (!value || value.length === 0) {
      return { required: true };
    }

    return null;
  };
}

@Component({
  selector: 'app-working-extra-hours-period-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatCheckboxModule,
    MatSelectModule,
  ],
  templateUrl: './working-extra-hours-period-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./working-extra-hours-period-dialog.component.css'],
})
export class WorkingExtraHoursPeriodDialogComponent {
  private destroy_ref = inject(DestroyRef);
  private fb = inject(UntypedFormBuilder);
  private api = inject(ApiService);
  private utils = inject(UtilsService);
  private dialogRef = inject(MatDialogRef<WorkingExtraHoursPeriodDialogComponent>);

  form: FormGroup;
  minDate: Date = new Date();
  readonly saving = signal(false);

  creationContext = toSignal(this.api.getWorkingExtraHoursPeriodCreationContext(), {
    initialValue: { requires_at_least_one_company_product: false },
  });

  projectsResponse = toSignal(this.api.getCompanyProducts(), {
    initialValue: {
      data: [] as CompanyProduct[],
      meta: {
        total: 0,
        per_page: 0,
        current_page: 1,
        from: 0,
        last_page: 1,
        links: [],
        path: '',
        to: 0,
      },
      links: { first: '', last: '', next: undefined, prev: undefined },
    } as ArrayResponse<CompanyProduct>,
  });

  company_products = computed(() => this.projectsResponse()?.data ?? []);

  requiresAtLeastOneCompanyProduct = computed(() => this.creationContext().requires_at_least_one_company_product);

  constructor() {
    this.form = this.fb.group({
      start_date: ['', Validators.required],
      end_date: ['', Validators.required],
      max_weekly_hours: [null],
      is_remote_working_allowed: [false],
      company_product_ids: [[] as number[]],
      justification: ['', Validators.required],
    });

    effect(() => {
      const company_product_ids_control = this.form.controls['company_product_ids'];

      if (this.requiresAtLeastOneCompanyProduct()) {
        company_product_ids_control.setValidators([atLeastOneCompanyProductSelectedValidator()]);
      } else {
        company_product_ids_control.clearValidators();
      }

      company_product_ids_control.updateValueAndValidity({ emitEvent: false });
    });
  }

  save(): void {
    if (this.form.invalid || this.saving()) return;

    this.saving.set(true);
    this.dialogRef.disableClose = true;

    const data = { ...this.form.value };

    if (data.start_date) data.start_date = this.utils.dateToStringYYYYMMDD(data.start_date);
    if (data.end_date) data.end_date = this.utils.dateToStringYYYYMMDD(data.end_date);

    if (!Array.isArray(data.company_product_ids)) {
      data.company_product_ids = [];
    }

    this.api
      .createWorkingExtraHoursPeriod(data)
      .pipe(
        takeUntilDestroyed(this.destroy_ref),
        finalize((): void => {
          this.saving.set(false);
          this.dialogRef.disableClose = false;
        })
      )
      .subscribe({
        next: (res: WorkingExtraHoursPeriod): void => this.dialogRef.close(res),
      });
  }
}
